import { MdlDialogReference, MdlDialogService } from '@angular-mdl/core';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { isMoment } from 'moment';
import { Observable } from 'rxjs';
import { Settings } from '../../../../core';
import { Category, CategoryNaturalKey, Report } from '../../../../documents';
import { Project } from '../../../shared';
import { UploadedDocumentsStorage } from '../../storages/uploaded-documents-storage';
import { UploadDialogData, UPLOAD_DLG_DATA_TOKEN } from '../base/upload-dialog-data';
import { SiteSpecificUploadDialogBase } from './site-specific-upload-dialog-base';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-lab-manuals-upload',
    template: require('./lab-manuals-upload.component.html'),
    styles: [require('./lab-manuals-upload.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LabManualsUploadComponent extends SiteSpecificUploadDialogBase {
    public readonly subcategoryCtrl: FormControl;
    public readonly projectCtrl: FormControl;
    public readonly versionNumberCtrl: FormControl;
    public readonly versionDateCtrl: FormControl;
    public readonly titleControl: FormControl;
    public readonly allowedFileExtensions: string[];

    public readonly subcategories$: Observable<Category[]>;

    public get showVersionNumber() {
        return this.editorForm.get('versionNumber') != null;
    }

    public get showVersionDate() {
        return this.editorForm.get('versionDate') != null;
    }

    public get showTitle() {
        return this.editorForm.get('title') != null;
    }

    public get categoryChangeConfirmationText() {
        const controlsWithValue = [];

        if (this.selectedFiles && this.selectedFiles.size > 0) {
            controlsWithValue.push('Files');
        }
        if (this.showVersionNumber && this.versionNumberCtrl.value) {
            controlsWithValue.push('Version Number');
        }
        if (this.showVersionDate && this.versionDateCtrl.value) {
            controlsWithValue.push('Version Date');
        }
        if (this.showTitle && this.titleControl.value) {
            controlsWithValue.push('Title');
        }
        
        return controlsWithValue.length > 0 ? `${controlsWithValue.join(' and ')} will be cleared.` : null;
    }

    private readonly studyManualsId: CategoryNaturalKey = 'study-manuals';
    private readonly synopsisId: CategoryNaturalKey = 'synopsis';
    private readonly labManualsOthersId: CategoryNaturalKey = 'lab-manuals-others';

    constructor(
        protected readonly dialog: MdlDialogReference,
        protected readonly dialogService: MdlDialogService,
        @Inject(UPLOAD_DLG_DATA_TOKEN) protected readonly dialogData: UploadDialogData<Report>,
        uploadedDocumentsStorage: UploadedDocumentsStorage,
        protected readonly settings: Settings
    ) {
        super(dialog, dialogService, uploadedDocumentsStorage, uploadedDocumentsStorage);

        this.subcategoryCtrl = new FormControl();
        this.projectCtrl = new FormControl();
        this.versionNumberCtrl = new FormControl();
        this.versionDateCtrl = new FormControl(null, Validators.required);
        this.titleControl = new FormControl();

        this.editorForm.addControl('subcategory', this.subcategoryCtrl);
        this.editorForm.addControl('project', this.projectCtrl);

        this.subcategories$ = dialogData.subCategories$;

        this.allowedFileExtensions = settings.uploadedFileExtensionsByCategoryId(dialogData.category.categoryId);

        this.subcategoryCtrl.valueChanges
            .pipe(takeUntil(this.onDialogDestroying))
            .subscribe((subcategoryId: CategoryNaturalKey) => {
                this.versionNumberCtrl.reset();
                this.versionDateCtrl.reset();
                this.titleControl.reset();
                super.resetFilesControl();

                switch (subcategoryId) {
                    case this.studyManualsId:
                        this.editorForm.addControl('versionNumber', this.versionNumberCtrl);
                        this.editorForm.removeControl('versionDate');
                        this.editorForm.removeControl('title');
                        break;

                    case this.synopsisId:
                        this.editorForm.removeControl('versionNumber');
                        this.editorForm.addControl('versionDate', this.versionDateCtrl);
                        this.editorForm.removeControl('title');
                        break;

                    case this.labManualsOthersId:
                        this.editorForm.removeControl('versionNumber');
                        this.editorForm.removeControl('versionDate');
                        this.editorForm.addControl('title', this.titleControl);
                        break;
                }
            });
    }

    public async addFiles() {
        const project = this.projectCtrl.value[0] as Project;
        const message = `The document will be posted to ${project.sponsor} - ${project.protocolNumber} - ${project.zcProjectNumber}. Proceed?`;

        const versionDate = isMoment(this.versionDateCtrl.value) && this.versionDateCtrl.value.isValid()
            ? this.versionDateCtrl.value.format('YYYY-MM-DD')
            : null;

        const documentMetadata = {
            subProtocolId: project.subProtocolId,
            zcProjectNumber: project.zcProjectNumber,
            protocolNumber: project.protocolNumber,
            versionNumber: this.versionNumberCtrl.value,
            versionDate: versionDate,
            title: this.titleControl.value,
        };

        super.addFiles(message, this.subcategoryCtrl.value, documentMetadata);
    }
}